/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Column's common component
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import type { IFixedColumn, IFixedColumns } from '@modules/app/hoc/WithFixedColumns/helpers';
import { guardComponentType } from '@modules/role/reducer/guardReducer';

import { TABLE_WIDTH_MAX, TABLE_WIDTH_MIN } from '../../constants';
import { getColumnCell, getColumnCellClass, getColumnCellRowCount } from '../../helpers/tableColumn';
import { ETableColumnUtilHideFilters } from '../../TableColumnUtil';
import { ColumnFieldType, ColumnTranslationType } from '../../types/columnTypes';
import ColumnFilter from '../Filters/ColumnFilter';
import { CellStyled } from './styled/ColumnsStyled';
import TableColumnFooter from './TableColumnFooter/TableColumnFooter';

/**
 * @description Props for column
 * @interface IColumn
 * @property {IFilterSort} current sort field and direction
 * @property {IPagePersonalAccountLocaleTable['messages']} messages locale
 * @property {(e: string) => void} onSort - sorting method
 * @property {(e: IOnFilterChangeParam) => void} onChange - fitler change method
 */
export interface IColumn<Field extends ColumnFieldType, Translation extends ColumnTranslationType> {
    cellType: string;
    componentsFeature: guardComponentType;
    filterType: string;
    fixed: string | boolean | undefined;
    hideFiltersType?: ETableColumnUtilHideFilters;
    isDisableFilter?: boolean;
    isDisableSort?: boolean;
    label?: string;
    name: Field;
    resizable: boolean;
    tooltipMsg?: string;
    translation: Translation;
    width: number;
}

/**
 * @description Header constructor return function
 * @interface IColumn
 */
const HeaderConstructor =
    <Field extends ColumnFieldType, Translation extends ColumnTranslationType>
    (props: IColumn<Field, Translation>) =>
        <CellStyled>
            <ColumnFilter
                {...props}
                hideFilterType={props.hideFiltersType}
                isDisableSort={props.isDisableSort}
            />
        </CellStyled>;

/**
 * @description Footer constructor return function
 * @interface IColumn
 */
const FooterConstructor =
    <Field extends ColumnFieldType, Translation extends ColumnTranslationType>
    (props: IColumn<Field, Translation>) => (data: {
        column: IFixedColumn;
        columns: IFixedColumns;
    }) => {
        return (
            <TableColumnFooter {...props} data={data}/>
        );
    };


/**
 * @description setting the unpaidBillsCount column
 * @interface IColumn
 */
const Columns =
    <Field extends ColumnFieldType, Translation extends ColumnTranslationType>
    (props: IColumn<Field, Translation>) => ({
        accessor: props.name,
        Cell: getColumnCell(props),
        className: getColumnCellClass(props),
        fixed: props.fixed,
        Footer: FooterConstructor(props),
        Header: HeaderConstructor(props),
        maxWidth: TABLE_WIDTH_MAX,
        minResizeWidth: TABLE_WIDTH_MIN,
        minWidth: TABLE_WIDTH_MIN,
        resizable: props.resizable,
        rowCount: getColumnCellRowCount(props),
        width: props.width,
    });


export default Columns;
